import { DownloadFile } from '@app/routes/content/routes/recordings/components/icons';
import { useAppDispatch, useAppSelector } from '@app/store';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { Fragment, useCallback, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useRecordingAnalytics } from '../../../hooks';
import {
  selectRecordingTranscriptionStatus,
  selectTranscriptSearchQuery,
} from '../../../selector';

const SEARCH_DEBOUNCE_MS = 300;

const HeaderSection = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const inputRef = useRef<HTMLInputElement>(null);

  const transcriptionStatus = useAppSelector(
    selectRecordingTranscriptionStatus,
  );
  const searchQuery = useAppSelector(selectTranscriptSearchQuery);
  const isTranscriptReady = transcriptionStatus === 'ready';

  const debouncedDispatch = useDebouncedCallback((query: string) => {
    dispatch({
      type: '[recording] update transcript search query',
      payload: { searchQuery: query },
    });
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = useCallback(() => {
    if (inputRef.current) {
      debouncedDispatch(inputRef.current.value);
    }
  }, [debouncedDispatch]);

  const handleDownloadTxt = () => {
    trackRecordingEvent('ch_recording_editor_transcript_downloaded_txt');
    dispatch({
      type: '[recording] download txt clicked',
    });
  };

  const handleDownloadVtt = () => {
    trackRecordingEvent('ch_recording_editor_transcript_downloaded_vtt');
    dispatch({
      type: '[recording] download vtt clicked',
    });
  };

  return (
    <div className="flex items-center gap-2 pb-3 z-[1] px-4">
      <div className="flex-1 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-4 w-4 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          defaultValue={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search"
          className="
            block w-full pl-10 pr-3 py-2 bg-spring-neutral-base border border-spring-neutral-b4
            rounded-lg text-sm placeholder-gray-400 text-white h-9
            focus:outline-none focus:border-spring-primary
          "
        />
      </div>
      <Menu as="div" className="relative inline-block text-left z-10">
        <div>
          <Menu.Button className="flex items-center text-sm font-medium text-spring-neutral-b0 rounded-10 hover:bg-spring-neutral-b0/10 transition-colors overflow-hidden">
            <DownloadFile className="size-5" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 max-w-80 w-max origin-top-right rounded-lg bg-spring-neutral-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600">
            <div className="p-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-gray-700' : ''
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                    onClick={handleDownloadVtt}
                    disabled={!isTranscriptReady}
                    title={
                      !isTranscriptReady
                        ? t(
                            'content-recording.content-menu.transcript-not-ready',
                          )
                        : t('content-recording.content-menu.download-vtt')
                    }
                    data-testid="transcript-download-vtt-button"
                  >
                    {t('content-recording.content-menu.download-vtt')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-gray-700' : ''
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                    onClick={handleDownloadTxt}
                    disabled={!isTranscriptReady}
                    title={
                      !isTranscriptReady
                        ? t(
                            'content-recording.content-menu.transcript-not-ready',
                          )
                        : t('content-recording.content-menu.download-txt')
                    }
                    data-testid="transcript-download-txt-button"
                  >
                    {t('content-recording.content-menu.download-txt')}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export { HeaderSection };
